import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from '@/i18n'
import { TOUR_TOOLTIP_Z } from '../constants'
import type { TourStep } from './tourRegistry'

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

const GAP = 12
const VIEWPORT_MARGIN = 8

export interface TooltipPosition {
  top: number
  left: number
  placement: TooltipPlacement
}

/**
 * Pure placement math (unit-testable): position a tooltip of
 * `tooltipSize` next to `targetRect` on the preferred side, flipping to
 * the opposite side when it would overflow, then clamping into the
 * viewport.
 */
export function computeTooltipPosition(
  targetRect: { top: number; left: number; width: number; height: number },
  tooltipSize: { width: number; height: number },
  preferred: TooltipPlacement,
  viewport: { width: number; height: number }
): TooltipPosition {
  const fits = (placement: TooltipPlacement): boolean => {
    switch (placement) {
      case 'top':
        return targetRect.top - GAP - tooltipSize.height >= VIEWPORT_MARGIN
      case 'bottom':
        return (
          targetRect.top + targetRect.height + GAP + tooltipSize.height <=
          viewport.height - VIEWPORT_MARGIN
        )
      case 'left':
        return targetRect.left - GAP - tooltipSize.width >= VIEWPORT_MARGIN
      case 'right':
        return (
          targetRect.left + targetRect.width + GAP + tooltipSize.width <=
          viewport.width - VIEWPORT_MARGIN
        )
    }
  }

  const OPPOSITE: Record<TooltipPlacement, TooltipPlacement> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  }
  const placement = fits(preferred) || !fits(OPPOSITE[preferred]) ? preferred : OPPOSITE[preferred]

  let top: number
  let left: number
  switch (placement) {
    case 'top':
      top = targetRect.top - GAP - tooltipSize.height
      left = targetRect.left + targetRect.width / 2 - tooltipSize.width / 2
      break
    case 'bottom':
      top = targetRect.top + targetRect.height + GAP
      left = targetRect.left + targetRect.width / 2 - tooltipSize.width / 2
      break
    case 'left':
      top = targetRect.top + targetRect.height / 2 - tooltipSize.height / 2
      left = targetRect.left - GAP - tooltipSize.width
      break
    case 'right':
      top = targetRect.top + targetRect.height / 2 - tooltipSize.height / 2
      left = targetRect.left + targetRect.width + GAP
      break
  }

  // Clamp into the viewport.
  left = Math.min(
    Math.max(left, VIEWPORT_MARGIN),
    Math.max(viewport.width - tooltipSize.width - VIEWPORT_MARGIN, VIEWPORT_MARGIN)
  )
  top = Math.min(
    Math.max(top, VIEWPORT_MARGIN),
    Math.max(viewport.height - tooltipSize.height - VIEWPORT_MARGIN, VIEWPORT_MARGIN)
  )

  return { top, left, placement }
}

const ARROW_CLASS: Record<TooltipPlacement, string> = {
  // Arrow sits on the edge FACING the target.
  bottom: '-top-1 left-1/2 -translate-x-1/2',
  top: '-bottom-1 left-1/2 -translate-x-1/2',
  right: '-left-1 top-1/2 -translate-y-1/2',
  left: '-right-1 top-1/2 -translate-y-1/2',
}

/**
 * Dark coach-mark card anchored to the spotlit target (Figma "Let's get
 * started" tooltip): bold title, body, step counter, Skip and Next/Done.
 */
const TourTooltip = ({
  step,
  targetRect,
  stepNumber,
  stepCount,
  onNext,
  onSkip,
}: {
  step: TourStep
  targetRect: DOMRect
  stepNumber: number
  stepCount: number
  onNext: () => void
  onSkip: () => void
}) => {
  const { t } = useTranslation('help_agent')
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<TooltipPosition | null>(null)

  useLayoutEffect(() => {
    const tooltip = ref.current
    if (!tooltip) return
    setPosition(
      computeTooltipPosition(
        targetRect,
        { width: tooltip.offsetWidth, height: tooltip.offsetHeight },
        step.placement ?? 'bottom',
        { width: window.innerWidth, height: window.innerHeight }
      )
    )
  }, [targetRect, step])

  const isLast = stepNumber === stepCount

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={t(step.titleKey)}
      className={`fixed ${TOUR_TOOLTIP_Z} w-72 rounded-lg bg-gray-900 p-4 text-white shadow-xl`}
      style={
        position
          ? { top: position.top, left: position.left }
          : { top: -9999, left: -9999, visibility: 'hidden' }
      }
    >
      <div
        className={`absolute size-2.5 rotate-45 bg-gray-900 ${ARROW_CLASS[position?.placement ?? step.placement ?? 'bottom']}`}
      />
      <div className="text-sm font-semibold">{t(step.titleKey)}</div>
      <p className="mt-1 text-sm text-gray-300">{t(step.bodyKey)}</p>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {t('tour.step_counter', { current: stepNumber, total: stepCount })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="rounded px-2 py-1 text-xs text-gray-300 hover:text-white"
          >
            {t('tour.skip')}
          </button>
          <button
            type="button"
            onClick={onNext}
            className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-gray-900 hover:bg-gray-200"
          >
            {isLast ? t('tour.done') : t('tour.next')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TourTooltip
